<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';
import ForwardAPI from './api';
import { WASAPI_FORWARD_MESSAGE } from './constants';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const MAX_TARGETS = 10;

const { t } = useI18n();
const router = useRouter();
const inboxes = useMapGetter('inboxes/getInboxes');

const dialogRef = ref(null);
const message = ref(null);
const inboxId = ref('');
const query = ref('');
const results = ref([]);
const selected = ref([]);
const isSearching = ref(false);
const isSending = ref(false);
let searchTimer = null;

const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const preview = computed(() => {
  if (!message.value) return '';
  if (message.value.content) return message.value.content;
  const count = message.value.attachments?.length || 0;
  return t('WASAPI_FORWARD.ATTACHMENTS_ONLY', { count });
});

const open = payload => {
  message.value = payload;
  inboxId.value = payload.inboxId || '';
  query.value = '';
  results.value = [];
  selected.value = [];
  dialogRef.value?.open();
};

useEmitter(WASAPI_FORWARD_MESSAGE, open);

const search = value => {
  query.value = value;
  clearTimeout(searchTimer);
  if (!value || value.length < 2) {
    results.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    isSearching.value = true;
    try {
      const { data } = await ContactAPI.search(value, 1, 'name');
      results.value = (data.payload || []).filter(
        contact => !selected.value.some(s => s.id === contact.id)
      );
    } catch {
      results.value = [];
    } finally {
      isSearching.value = false;
    }
  }, 300);
};

const add = contact => {
  if (selected.value.length >= MAX_TARGETS) {
    useAlert(t('WASAPI_FORWARD.MAX_TARGETS', { count: MAX_TARGETS }));
    return;
  }
  selected.value = [...selected.value, contact];
  results.value = results.value.filter(c => c.id !== contact.id);
};

const remove = contact => {
  selected.value = selected.value.filter(c => c.id !== contact.id);
};

const canSend = computed(
  () => !!inboxId.value && selected.value.length > 0 && !isSending.value
);

const send = async () => {
  if (!canSend.value) return;
  isSending.value = true;
  try {
    const targets = selected.value.map(contact => ({
      contact_id: contact.id,
      inbox_id: inboxId.value,
    }));
    const { data } = await ForwardAPI.forward(
      message.value.conversationId,
      message.value.id,
      targets
    );
    const sent = data.results.filter(r => r.status === 'sent');
    const failed = data.results.filter(r => r.status !== 'sent');
    if (failed.length) {
      useAlert(
        t('WASAPI_FORWARD.PARTIAL', {
          sent: sent.length,
          failed: failed.length,
        }) + ` ${failed.map(r => r.error).join(' · ')}`
      );
    } else {
      useAlert(t('WASAPI_FORWARD.SUCCESS', { count: sent.length }));
    }
    dialogRef.value?.close();
    if (sent.length === 1 && data.results.length === 1) {
      router.push({
        name: 'inbox_conversation',
        params: { conversation_id: sent[0].conversation_id },
      });
    }
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WASAPI_FORWARD.ERROR'));
  } finally {
    isSending.value = false;
  }
};

const contactLabel = contact =>
  [contact.name, contact.phone_number || contact.email]
    .filter(Boolean)
    .join(' · ');
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="t('WASAPI_FORWARD.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <div
        class="p-3 text-sm rounded-lg bg-n-alpha-1 text-n-slate-11 line-clamp-3"
      >
        {{ preview }}
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_FORWARD.INBOX') }}
        </label>
        <ComboBox v-model="inboxId" :options="inboxOptions" class="w-full" />
      </div>

      <div class="flex flex-col gap-2">
        <Input
          :model-value="query"
          :label="t('WASAPI_FORWARD.CONTACTS')"
          :placeholder="t('WASAPI_FORWARD.SEARCH_PLACEHOLDER')"
          @input="search($event.target.value)"
        />
        <div v-if="selected.length" class="flex flex-wrap gap-2">
          <span
            v-for="contact in selected"
            :key="contact.id"
            class="flex items-center gap-1.5 pl-1 pr-2 py-1 text-xs rounded-full bg-n-brand/10 text-n-slate-12"
          >
            <Avatar
              :name="contact.name"
              :src="contact.thumbnail"
              :size="18"
              rounded-full
            />
            {{ contact.name }}
            <button
              type="button"
              class="grid place-content-center size-4 rounded-full hover:bg-n-alpha-2"
              :aria-label="t('DIALOG.BUTTONS.CANCEL')"
              @click="remove(contact)"
            >
              <Icon icon="i-lucide-x" class="size-3" />
            </button>
          </span>
        </div>
        <ul
          v-if="results.length"
          class="flex flex-col max-h-48 overflow-y-auto rounded-lg outline outline-1 outline-n-weak divide-y divide-n-weak"
        >
          <li v-for="contact in results" :key="contact.id">
            <button
              type="button"
              class="flex items-center w-full gap-2 px-3 py-2 text-sm text-left hover:bg-n-alpha-1"
              @click="add(contact)"
            >
              <Avatar
                :name="contact.name"
                :src="contact.thumbnail"
                :size="24"
                rounded-full
              />
              <span class="truncate">{{ contactLabel(contact) }}</span>
            </button>
          </li>
        </ul>
        <p
          v-else-if="query.length >= 2 && !isSearching"
          class="text-xs text-n-slate-10"
        >
          {{ t('WASAPI_FORWARD.NO_RESULTS') }}
        </p>
      </div>
      <p class="text-xs text-n-slate-10">
        {{ t('WASAPI_FORWARD.WINDOW_HINT') }}
      </p>
    </div>
    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="dialogRef.close()"
        />
        <Button
          :label="t('WASAPI_FORWARD.SEND', { count: selected.length })"
          color="blue"
          icon="i-lucide-forward"
          :disabled="!canSend"
          :is-loading="isSending"
          @click="send"
        />
      </div>
    </template>
  </Dialog>
</template>
